import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";
import { Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function FollowListDialog({ open, onOpenChange, userId, mode, ownerName, onChanged }) {
  const navigate = useNavigate();
  const { setFollowing } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState({}); // id -> bool

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${userId}/${mode}`);
        if (!cancelled) setRows(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, mode]);

  const openProfile = (id) => {
    onOpenChange(false);
    navigate(`/app/scholars/${id}`);
  };

  const toggleFollow = async (row) => {
    if (pending[row.id]) return;
    const wasFollowing = row.followed_by_me;
    setPending((p) => ({ ...p, [row.id]: true }));
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, followed_by_me: !wasFollowing } : r)));
    try {
      if (wasFollowing) await api.delete(`/users/${row.id}/follow`);
      else await api.post(`/users/${row.id}/follow`);
      setFollowing(row.id, !wasFollowing);
      if (onChanged) onChanged();
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, followed_by_me: wasFollowing } : r)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPending((p) => ({ ...p, [row.id]: false }));
    }
  };

  const title = mode === "followers" ? "Followers" : "Following";
  const subtitle =
    mode === "followers"
      ? `Scholars who follow ${ownerName || "this scholar"}.`
      : `Scholars ${ownerName || "this scholar"} follows.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`follow-list-dialog-${mode}`}
        className="bg-[color:var(--ayur-cream)] max-w-md max-h-[80vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] inline-flex items-center gap-2">
            <Users className="w-5 h-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-[color:var(--ayur-mute)]">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 space-y-2" data-testid={`follow-list-body-${mode}`}>
          {loading && <div className="py-8 text-center text-[color:var(--ayur-mute)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="py-10 text-center text-[color:var(--ayur-mute)]">
              {mode === "followers" ? "No followers yet." : "Not following anyone yet."}
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`follow-list-row-${r.id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-[color:var(--ayur-border)] hover:bg-white transition"
            >
              <button onClick={() => openProfile(r.id)} className="shrink-0">
                <ScholarAvatar user={r} size={44} />
              </button>
              <button onClick={() => openProfile(r.id)} className="flex-1 text-left min-w-0">
                <div className="font-medium text-[color:var(--ayur-ink)] inline-flex items-center gap-1.5 truncate">
                  <span className="truncate">{r.full_name}</span>
                  {r.verified && <VerifiedBadge size={14} />}
                </div>
                <div className="text-xs text-[color:var(--ayur-mute)] truncate">
                  {r.degree}
                  {r.specialty ? ` · ${r.specialty}` : ""}
                  {r.batch_year ? ` · Batch ${r.batch_year}` : ""}
                </div>
                <div className="text-xs text-[color:var(--ayur-mute)] truncate">{r.college}</div>
              </button>
              {!r.is_me && (
                <Button
                  data-testid={`follow-list-toggle-${r.id}`}
                  size="sm"
                  onClick={() => toggleFollow(r)}
                  disabled={!!pending[r.id]}
                  className={
                    r.followed_by_me
                      ? "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-badge)] border border-[color:var(--ayur-border)]"
                      : "bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"
                  }
                >
                  {pending[r.id] ? "…" : r.followed_by_me ? "Following" : "Follow"}
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
